// The async clipboard API only reliably accepts image/png, so anything else
// (jpg, webp, ...) is re-encoded through a canvas first.
async function toPngBlob(blob: Blob): Promise<Blob> {
  if (blob.type === "image/png") return blob;
  const bitmap = await createImageBitmap(blob);
  try {
    const canvas = document.createElement("canvas");
    canvas.width = bitmap.width;
    canvas.height = bitmap.height;
    const ctx = canvas.getContext("2d");
    if (!ctx) throw new Error("Canvas unavailable");
    ctx.drawImage(bitmap, 0, 0);
    return await new Promise<Blob>((resolve, reject) =>
      canvas.toBlob((b) => (b ? resolve(b) : reject(new Error("PNG encode failed"))), "image/png")
    );
  } finally {
    bitmap.close();
  }
}

export async function copyImageToClipboard(url: string): Promise<void> {
  if (!navigator.clipboard || typeof ClipboardItem === "undefined") {
    throw new Error("Clipboard not supported");
  }
  // Handing ClipboardItem a promise keeps the write inside the user gesture
  // (needed by Safari) while the image downloads.
  // `cache: "reload"`: the preview's <img> already loaded this URL without CORS,
  // and the browser would otherwise reuse that cached response, which has no
  // Access-Control-Allow-Origin header, and fail this fetch.
  const pngPromise = fetch(url, { cache: "reload" })
    .then((res) => {
      if (!res.ok) throw new Error(`Image fetch failed (${res.status})`);
      return res.blob();
    })
    .then(toPngBlob);
  await navigator.clipboard.write([new ClipboardItem({ "image/png": pngPromise })]);
}
